import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Plus, Power, PowerOff, UserCog, Loader2, Pencil, Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { listSucursales } from "@/services/sucursalesService";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";

const ROL_BADGE = {
  SuperAdmin: "bg-[#0A0A0A] text-[#FFCC00]",
  TenantAdmin: "bg-[#FFCC00] text-[#0A0A0A]",
  Gerente: "bg-purple-50 text-purple-700 border border-purple-200",
  Cajero: "bg-[#10B981]/10 text-[#0f7a5b] border border-[#10B981]/40",
  Valet: "bg-blue-50 text-blue-700 border border-blue-200",
};

export default function Usuarios() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rol, setRol] = useState("Cajero");
  const [editing, setEditing] = useState(null);
  const [confirmUser, setConfirmUser] = useState(null);
  const [rolChange, setRolChange] = useState(null); // usuario al que se le cambia el rol
  const [rolNuevo, setRolNuevo] = useState("");
  const [rolMotivo, setRolMotivo] = useState("");
  const [savingRol, setSavingRol] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [sucFilter, setSucFilter] = useState("todos");
  const [form, setForm] = useState({ email: "", password: "", usuario_corto: "", nip_acceso: "", id_sucursal: "", id_empresa: "", sucursales_ids: [], todas_las_sucursales: false, puede_realizar_corte: false, puede_registrar_gastos: false, puede_autorizar_cortesia: false });

  const isOp = rol === "Cajero" || rol === "Valet" || rol === "Gerente";
  const necesitaEmpresa = rol === "TenantAdmin"; // roles de tenant requieren empresa

  const toggleSuc = (id) => setForm((f) => ({
    ...f,
    sucursales_ids: f.sucursales_ids.includes(id) ? f.sucursales_ids.filter((x) => x !== id) : [...f.sucursales_ids, id],
  }));

  const emptyForm = { email: "", password: "", usuario_corto: "", nip_acceso: "", id_sucursal: "", id_empresa: "", sucursales_ids: [], todas_las_sucursales: false, puede_realizar_corte: false, puede_registrar_gastos: false, puede_autorizar_cortesia: false };
  const sucName = Object.fromEntries(sucursales.map((s) => [s.id_sucursal, s.nombre]));

  const openCreate = () => {
    setEditing(null);
    setRol(rolesDisponibles[0]);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setRol(u.rol);
    setForm({
      email: u.email || "",
      password: "",
      usuario_corto: u.usuario_corto || "",
      nip_acceso: "",
      id_sucursal: u.id_sucursal || "",
      id_empresa: u.id_empresa || "",
      sucursales_ids: u.sucursales_ids || [],
      todas_las_sucursales: !!u.todas_las_sucursales,
      puede_realizar_corte: !!u.puede_realizar_corte,
      puede_registrar_gastos: !!u.puede_registrar_gastos,
      puede_autorizar_cortesia: !!u.puede_autorizar_cortesia,
    });
    setOpen(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([api.get("/usuarios"), listSucursales()]);
      setItems(u.data); setSucursales(s.data);
      if (user?.rol === "SuperAdmin") {
        try {
          const e = await api.get("/empresas");
          setEmpresas((e.data || []).filter((x) => x.estatus !== "suspendido"));
        } catch { /* noop */ }
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        // EDICIÓN -> PUT /api/usuarios/{id} (el rol no se modifica)
        const upd = {};
        if (isOp) {
          if (!form.usuario_corto.trim()) { setSaving(false); return toast.error("Usuario corto requerido"); }
          if (form.nip_acceso && form.nip_acceso.length !== 4) { setSaving(false); return toast.error("El NIP debe tener 4 dígitos"); }
          if (!form.todas_las_sucursales && form.sucursales_ids.length === 0) { setSaving(false); return toast.error("Selecciona al menos una sucursal o activa 'Todas las sucursales'"); }
          upd.usuario_corto = form.usuario_corto.trim();
          if (form.nip_acceso) upd.nip_acceso = form.nip_acceso;
          upd.todas_las_sucursales = form.todas_las_sucursales;
          upd.puede_realizar_corte = form.puede_realizar_corte;
          upd.puede_registrar_gastos = form.puede_registrar_gastos;
          upd.puede_autorizar_cortesia = form.puede_autorizar_cortesia;
          upd.sucursales_ids = form.todas_las_sucursales ? [] : form.sucursales_ids;
          if (!form.todas_las_sucursales) upd.id_sucursal = form.sucursales_ids[0];
        } else {
          if (form.email.trim()) upd.email = form.email.trim();
          if (form.password) upd.password = form.password;
        }
        await api.put(`/usuarios/${editing.id_usuario}`, upd);
        toast.success("Usuario actualizado");
      } else {
        // CREACIÓN -> POST /api/usuarios
        const payload = { rol };
        if (isOp) {
          if (!form.usuario_corto.trim() || form.nip_acceso.length !== 4) { setSaving(false); return toast.error("Usuario y NIP de 4 dígitos requeridos"); }
          if (!form.todas_las_sucursales && form.sucursales_ids.length === 0) { setSaving(false); return toast.error("Selecciona al menos una sucursal o activa 'Todas las sucursales'"); }
          payload.usuario_corto = form.usuario_corto.trim();
          payload.nip_acceso = form.nip_acceso;
          payload.todas_las_sucursales = form.todas_las_sucursales;
          payload.puede_realizar_corte = form.puede_realizar_corte;
          payload.puede_registrar_gastos = form.puede_registrar_gastos;
          payload.puede_autorizar_cortesia = form.puede_autorizar_cortesia;
          if (form.todas_las_sucursales) {
            payload.sucursales_ids = [];
          } else {
            payload.sucursales_ids = form.sucursales_ids;
            payload.id_sucursal = form.sucursales_ids[0];
          }
        } else {
          if (!form.email.trim() || !form.password) { setSaving(false); return toast.error("Correo y contraseña requeridos"); }
          payload.email = form.email.trim();
          payload.password = form.password;
          if (necesitaEmpresa) {
            if (!form.id_empresa) { setSaving(false); return toast.error("Selecciona la empresa para el TenantAdmin"); }
            payload.id_empresa = form.id_empresa;
          } else {
            payload.id_empresa = null;
          }
        }
        await api.post("/usuarios", payload);
        toast.success("Usuario creado");
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleEstatusUsuario = async (u) => {
    try {
      if (u.estatus === "suspendido") {
        await api.post(`/usuarios/${u.id_usuario}/reactivar`);
        toast.success("Usuario reactivado");
      } else {
        await api.delete(`/usuarios/${u.id_usuario}`);
        toast.success("Usuario suspendido");
      }
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const rolesDisponibles = user?.rol === "SuperAdmin"
    ? ["TenantAdmin", "SuperAdmin", "Soporte", "Onboarding", "Auditor"]
    : ["Gerente", "Cajero", "Valet"];
  const ROL_OPT_LABEL = { Gerente: "Gerente / Supervisor" };

  const rolesAsignables = user?.rol === "SuperAdmin"
    ? ["TenantAdmin", "SuperAdmin", "Soporte", "Onboarding", "Auditor", "Gerente", "Cajero", "Valet"]
    : ["Gerente", "Cajero", "Valet"];

  // ¿El usuario en sesión puede cambiar el rol de "u"?
  const puedeCambiarRol = (u) => {
    if (u.id_usuario === user?.id_usuario) return false; // nunca su propio rol
    if (user?.rol === "SuperAdmin") return true;
    if (user?.rol === "TenantAdmin") return ["Gerente", "Cajero", "Valet"].includes(u.rol);
    return false;
  };

  const openRolChange = (u) => {
    setRolChange(u);
    setRolNuevo(u.rol);
    setRolMotivo("");
  };

  const guardarRol = async () => {
    if (!rolNuevo || rolNuevo === rolChange.rol) { toast.error("Selecciona un rol distinto al actual"); return; }
    setSavingRol(true);
    try {
      await api.patch(`/usuarios/${rolChange.id_usuario}/rol`, { rol: rolNuevo, motivo: rolMotivo.trim() || undefined });
      toast.success("Rol actualizado correctamente");
      setRolChange(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingRol(false); }
  };

  const q = query.trim().toLowerCase();
  const filtered = items.filter((u) => {
    const ident = `${u.email || ""} ${u.usuario_corto || ""}`.toLowerCase();
    const matchQ = !q || ident.includes(q);
    const est = u.estatus || "activo";
    const matchEst = statusFilter === "todos" || est === statusFilter;
    const matchSuc = sucFilter === "todos" || u.todas_las_sucursales || (u.sucursales_ids || []).includes(sucFilter);
    return matchQ && matchEst && matchSuc;
  });

  return (
    <DashboardLayout>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2">Accesos del inquilino</p>
            <h1 className="font-display text-4xl font-black tracking-tight">Usuarios</h1>
          </div>
          <button onClick={openCreate} data-testid="create-usuario-btn"
            className="btn-hard h-11 px-5 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm flex items-center gap-2">
            <Plus className="h-4 w-4" /> Nuevo usuario
          </button>
        </div>

        <div className="mb-4 flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative w-full max-w-sm">
            <Search className="h-4 w-4 text-[#52525B] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="usuarios-search-input"
              placeholder="Buscar por correo o usuario…" className="h-11 pl-9 rounded-none border-[#0A0A0A]" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="usuarios-status-filter" className="h-11 w-full lg:w-48 rounded-none border-[#0A0A0A]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos" data-testid="ufilter-status-todos">Todos los estatus</SelectItem>
              <SelectItem value="activo" data-testid="ufilter-status-activo">Activos</SelectItem>
              <SelectItem value="suspendido" data-testid="ufilter-status-suspendido">Suspendidos</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sucFilter} onValueChange={setSucFilter}>
            <SelectTrigger data-testid="usuarios-suc-filter" className="h-11 w-full lg:w-56 rounded-none border-[#0A0A0A]"><SelectValue placeholder="Sucursal" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos" data-testid="ufilter-suc-todos">Todas las sucursales</SelectItem>
              {sucursales.map((s) => <SelectItem key={s.id_sucursal} value={s.id_sucursal} data-testid={`ufilter-suc-${s.id_sucursal}`}>{s.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white border border-[#e4e4e7]">
          {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
          : items.length === 0 ? <div className="p-16 text-center text-[#52525B]"><UserCog className="h-8 w-8 mx-auto mb-3 opacity-40" /><p className="text-sm">Sin usuarios.</p></div>
          : filtered.length === 0 ? <div className="p-16 text-center text-[#52525B]" data-testid="usuarios-no-results"><Search className="h-8 w-8 mx-auto mb-3 opacity-40" /><p className="text-sm">Sin resultados con los filtros seleccionados.</p></div>
          : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="usuarios-table">
              <thead><tr className="border-b border-[#e4e4e7] text-left">
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Identificador</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Rol</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Alcance</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Estatus</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase text-right">Acciones</th>
              </tr></thead>
              <tbody>
                {filtered.map((u) => {
                  const esOp = ["Cajero", "Valet", "Gerente"].includes(u.rol);
                  return (
                  <tr key={u.id_usuario} className="border-b border-[#f0f0f1] hover:bg-gray-50 transition-colors duration-200">
                    <td className="px-5 py-4 font-semibold">{u.email || u.usuario_corto}</td>
                    <td className="px-5 py-4"><span className={`px-2 py-0.5 text-[10px] tracking-wider uppercase font-bold ${ROL_BADGE[u.rol]}`}>{u.rol}</span></td>
                    <td className="px-5 py-4" data-testid={`usuario-alcance-${u.id_usuario}`}>
                      {!esOp ? <span className="text-xs text-gray-400">—</span>
                       : u.todas_las_sucursales ? <span className="px-2 py-0.5 text-[10px] tracking-wider uppercase font-bold bg-[#0A0A0A] text-[#FFCC00]">Todas</span>
                       : (u.sucursales_ids || []).length === 0 ? <span className="text-xs text-gray-400">Sin asignar</span>
                       : <div className="flex flex-wrap gap-1">{(u.sucursales_ids || []).map((id) => <span key={id} className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#FAFAFA] border border-[#e4e4e7]">{sucName[id] || "?"}</span>)}</div>}
                    </td>
                    <td className="px-5 py-4">
                      <span data-testid={`usuario-estatus-${u.id_usuario}`} className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold ${u.estatus === "suspendido" ? "text-[#B45309]" : "text-[#047857]"}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${u.estatus === "suspendido" ? "bg-[#F59E0B]" : "bg-[#10B981]"}`} />
                        {u.estatus || "activo"}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => openEdit(u)} data-testid={`edit-usuario-${u.id_usuario}`}
                          className="h-8 w-8 inline-flex items-center justify-center border border-[#e4e4e7] hover:border-[#0A0A0A] transition-colors duration-200"><Pencil className="h-3.5 w-3.5" /></button>
                        {puedeCambiarRol(u) && (
                          <button onClick={() => openRolChange(u)} data-testid={`rol-usuario-${u.id_usuario}`}
                            className="h-8 px-3 inline-flex items-center gap-1.5 border border-[#e4e4e7] text-[11px] font-bold uppercase tracking-wider hover:border-[#0A0A0A] transition-colors duration-200"><ShieldCheck className="h-3.5 w-3.5" /> Rol</button>
                        )}
                        {u.id_usuario !== user?.id_usuario && (
                          <button onClick={() => setConfirmUser(u)} data-testid={`toggle-usuario-${u.id_usuario}`}
                            className={`h-8 px-3 inline-flex items-center gap-1.5 border text-[11px] font-bold uppercase tracking-wider transition-colors duration-200 ${u.estatus === "suspendido" ? "border-[#e4e4e7] text-[#047857] hover:border-[#10B981]" : "border-[#e4e4e7] text-[#B45309] hover:border-[#F59E0B]"}`}>
                            {u.estatus === "suspendido" ? <><Power className="h-3.5 w-3.5" /> Reactivar</> : <><PowerOff className="h-3.5 w-3.5" /> Suspender</>}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </motion.div>

      <Sheet open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader><SheetTitle className="font-display text-2xl font-black tracking-tight">{editing ? "Editar usuario" : "Nuevo usuario"}</SheetTitle><SheetDescription>{editing ? "Actualiza los datos del usuario. Deja NIP/contraseña en blanco para conservarlos." : "Crea un acceso administrativo (correo) u operativo (usuario + NIP)."}</SheetDescription></SheetHeader>
          <div className="space-y-5 mt-6">
            <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Rol</Label>
              <Select value={rol} onValueChange={setRol} disabled={!!editing}>
                <SelectTrigger data-testid="usuario-rol" className="h-11 rounded-none border-[#0A0A0A] disabled:opacity-70"><SelectValue /></SelectTrigger>
                <SelectContent>{rolesDisponibles.map((r) => <SelectItem key={r} value={r} data-testid={`rol-opt-${r}`}>{ROL_OPT_LABEL[r] || r}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {isOp ? (
              <>
                <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Usuario corto</Label>
                  <Input value={form.usuario_corto} onChange={(e) => setForm({ ...form, usuario_corto: e.target.value })} placeholder="caja02" data-testid="usuario-corto" className="h-11 rounded-none border-[#0A0A0A]" /></div>
                <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">NIP (4 dígitos)</Label>
                  <Input value={form.nip_acceso} maxLength={4} inputMode="numeric"
                    onChange={(e) => setForm({ ...form, nip_acceso: e.target.value.replace(/\D/g, "") })} placeholder="1234" data-testid="usuario-nip" className="h-11 rounded-none border-[#0A0A0A] font-display tracking-[0.5em]" /></div>
                <div className="space-y-3 border border-[#e4e4e7] p-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Asignar a todas las sucursales</Label>
                    <Switch checked={form.todas_las_sucursales} onCheckedChange={(v) => setForm({ ...form, todas_las_sucursales: v })}
                      data-testid="usuario-todas-sucursales" className="data-[state=checked]:bg-[#10B981]" />
                  </div>
                  {form.todas_las_sucursales ? (
                    <p className="text-xs text-[#52525B]">Perfil itinerante: tendrá acceso a todas las sucursales actuales y futuras.</p>
                  ) : (
                    <div className="space-y-2">
                      <Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Sucursales autorizadas</Label>
                      {sucursales.length === 0 ? (
                        <p className="text-xs text-[#52525B]">No hay sucursales disponibles.</p>
                      ) : (
                        <div className="max-h-52 overflow-y-auto space-y-px bg-[#e4e4e7] border border-[#e4e4e7]">
                          {sucursales.map((s) => (
                            <label key={s.id_sucursal} htmlFor={`suc-${s.id_sucursal}`}
                              className="flex items-center gap-3 bg-white px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors duration-150">
                              <Checkbox id={`suc-${s.id_sucursal}`} checked={form.sucursales_ids.includes(s.id_sucursal)}
                                onCheckedChange={() => toggleSuc(s.id_sucursal)} data-testid={`suc-check-${s.id_sucursal}`}
                                className="data-[state=checked]:bg-[#0A0A0A] data-[state=checked]:border-[#0A0A0A]" />
                              <span className="text-sm font-medium">{s.nombre}</span>
                            </label>
                          ))}
                        </div>
                      )}
                      {form.sucursales_ids.length > 0 && (
                        <p className="text-[11px] text-[#047857] font-bold" data-testid="usuario-suc-count">{form.sucursales_ids.length} sucursal(es) seleccionada(s)</p>
                      )}
                    </div>
                  )}
                </div>
                {/* Sprint 18.0 — permisos financieros del colaborador */}
                <div className="space-y-2 border border-[#e4e4e7] p-3" data-testid="usuario-permisos-block">
                  <Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Permisos financieros</Label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm font-medium">Puede realizar Corte de Caja</span>
                    <Switch checked={!!form.puede_realizar_corte} onCheckedChange={(v) => setForm((f) => ({ ...f, puede_realizar_corte: v }))}
                      data-testid="toggle-puede-corte" className="data-[state=checked]:bg-[#047857]" />
                  </label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm font-medium">Puede registrar Gastos / Nóminas</span>
                    <Switch checked={!!form.puede_registrar_gastos} onCheckedChange={(v) => setForm((f) => ({ ...f, puede_registrar_gastos: v }))}
                      data-testid="toggle-puede-gastos" className="data-[state=checked]:bg-[#047857]" />
                  </label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm font-medium">Puede autorizar Cortesías (cobro $0)</span>
                    <Switch checked={!!form.puede_autorizar_cortesia} onCheckedChange={(v) => setForm((f) => ({ ...f, puede_autorizar_cortesia: v }))}
                      data-testid="toggle-puede-cortesia" className="data-[state=checked]:bg-[#047857]" />
                  </label>
                </div>
              </>
            ) : (
              <>
                {necesitaEmpresa && (
                  <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Empresa</Label>
                    <Select value={form.id_empresa} onValueChange={(v) => setForm({ ...form, id_empresa: v })}>
                      <SelectTrigger data-testid="usuario-empresa" className="h-11 rounded-none border-[#0A0A0A]"><SelectValue placeholder="Asigna la empresa del TenantAdmin" /></SelectTrigger>
                      <SelectContent>{empresas.map((e) => <SelectItem key={e.id_empresa} value={e.id_empresa} data-testid={`empresa-opt-${e.id_empresa}`}>{e.nombre}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Correo</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="usuario-email" className="h-11 rounded-none border-[#0A0A0A]" /></div>
                <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Contraseña</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="usuario-password" className="h-11 rounded-none border-[#0A0A0A]" /></div>
              </>
            )}

            <button onClick={save} disabled={saving} data-testid="usuario-save-btn"
              className="btn-hard w-full h-12 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Guardar cambios" : "Crear usuario"}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirmUser !== null}
        onOpenChange={(o) => { if (!o) setConfirmUser(null); }}
        title={confirmUser?.estatus === "suspendido" ? "Reactivar usuario" : "Suspender usuario"}
        description={confirmUser?.estatus === "suspendido"
          ? `¿Deseas reactivar el acceso de "${confirmUser?.email || confirmUser?.usuario_corto}"?`
          : `¿Estás seguro de que deseas suspender el acceso del usuario "${confirmUser?.email || confirmUser?.usuario_corto}"?`}
        confirmLabel={confirmUser?.estatus === "suspendido" ? "Reactivar" : "Suspender"}
        danger={confirmUser?.estatus !== "suspendido"}
        onConfirm={() => { const u = confirmUser; setConfirmUser(null); if (u) toggleEstatusUsuario(u); }}
      />

      {/* Diálogo Cambiar rol (RBAC: SuperAdmin y TenantAdmin) */}
      <Dialog open={rolChange !== null} onOpenChange={(o) => { if (!o) setRolChange(null); }}>
        <DialogContent className="rounded-none border-[#0A0A0A]" data-testid="rol-change-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black tracking-tight">Cambiar rol</DialogTitle>
            <DialogDescription>{rolChange?.email || rolChange?.usuario_corto} · rol actual: <b>{rolChange?.rol}</b></DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Nuevo rol</Label>
              <Select value={rolNuevo} onValueChange={setRolNuevo}>
                <SelectTrigger data-testid="rol-change-select" className="h-11 rounded-none border-[#0A0A0A]"><SelectValue placeholder="Selecciona un rol" /></SelectTrigger>
                <SelectContent>{rolesAsignables.map((r) => <SelectItem key={r} value={r} data-testid={`rol-change-opt-${r}`}>{ROL_OPT_LABEL[r] || r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Motivo (opcional)</Label>
              <Input value={rolMotivo} onChange={(e) => setRolMotivo(e.target.value)} data-testid="rol-change-motivo" placeholder="Ej. Ascenso a supervisor" className="h-11 rounded-none border-[#0A0A0A]" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setRolChange(null)} className="h-11 px-5 border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm">Cancelar</button>
            <button onClick={guardarRol} disabled={savingRol} data-testid="rol-change-save"
              className="btn-hard h-11 px-5 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2 disabled:opacity-60">
              {savingRol ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar rol"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
